import React, { useState } from 'react';
import { useListContext } from 'react-admin';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { KeyboardArrowDown, KeyboardArrowRight } from '@material-ui/icons';

import { ChipField, QuizExpandPanel, QuizTitle } from 'resources/common';
import {
  QuizProgressField,
  QuizScoreDistributionField,
  WeakestQuestionField,
} from 'resources/common/cohorts/list/fields';

const COLUMN_COUNT = 6;

const useStyles = makeStyles({
  scores: {
    width: 250,
    minWidth: 250,
    maxWidth: 250,
  },
  averageScore: { width: 100 },
  weakestQuestion: { width: 160 },
  expandCell: {
    width: 48,
    paddingLeft: 0,
    paddingRight: 8,
    textAlign: 'right',
  },
  row: {
    cursor: 'pointer',
  },
});

const ExpandableQuizTable = ({ course, cellClassNames = {} }) => {
  const classes = useStyles();
  const { data, ids, currentSort, setSort } = useListContext();
  const [expandedQuizIds, setExpandedQuizIds] = useState([]);
  const [navigation, setNavigation] = useState(null);

  const handleSort = (field) => {
    const order =
      currentSort.field === field && currentSort.order === 'ASC' ? 'DESC' : 'ASC';
    setSort(field, order);
  };

  const toggleQuiz = (quizId) => {
    setExpandedQuizIds((current) =>
      current.includes(quizId)
        ? current.filter((id) => id !== quizId)
        : [...current, quizId]
    );
  };

  const handleWeakestNavigate = (quiz, questionIndex) => {
    setExpandedQuizIds((current) =>
      current.includes(quiz.id) ? current : [...current, quiz.id]
    );
    setNavigation({
      quizId: quiz.id,
      questionIndex,
      requestId: Date.now(),
    });
  };

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Path</TableCell>
          <TableCell>Submitted</TableCell>
          <TableCell className={cellClassNames.scores || classes.scores}>
            Score Distribution
          </TableCell>
          <TableCell
            className={cellClassNames.averageScore || classes.averageScore}
            sortDirection={
              currentSort.field === 'average_score'
                ? currentSort.order.toLowerCase()
                : false
            }
          >
            <TableSortLabel
              active={currentSort.field === 'average_score'}
              direction={
                currentSort.field === 'average_score'
                  ? currentSort.order.toLowerCase()
                  : 'asc'
              }
              onClick={() => handleSort('average_score')}
            >
              Avg. Score
            </TableSortLabel>
          </TableCell>
          <TableCell
            className={
              cellClassNames.weakestQuestion || classes.weakestQuestion
            }
          >
            Weakest question
          </TableCell>
          <TableCell className={classes.expandCell} />
        </TableRow>
      </TableHead>
      <TableBody>
        {ids.map((quizId) => {
          const record = data[quizId];
          if (!record) return null;
          const isExpanded = expandedQuizIds.includes(quizId);
          return (
            <React.Fragment key={quizId}>
              <TableRow
                hover
                className={classes.row}
                onClick={() => toggleQuiz(quizId)}
              >
                <TableCell>
                  <QuizTitle record={record} source="path" />
                </TableCell>
                <TableCell>
                  <QuizProgressField record={record} source="quiz_progress" />
                </TableCell>
                <TableCell className={cellClassNames.scores || classes.scores}>
                  <QuizScoreDistributionField
                    record={record}
                    source="submissions"
                  />
                </TableCell>
                <TableCell
                  className={
                    cellClassNames.averageScore || classes.averageScore
                  }
                >
                  <ChipField
                    record={record}
                    source="average_score"
                    chipStyle="assignmentGrade"
                  />
                </TableCell>
                <TableCell
                  className={
                    cellClassNames.weakestQuestion || classes.weakestQuestion
                  }
                >
                  <WeakestQuestionField
                    record={record}
                    onNavigate={handleWeakestNavigate}
                  />
                </TableCell>
                <TableCell className={classes.expandCell}>
                  {isExpanded ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                </TableCell>
              </TableRow>
              {isExpanded && (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT}>
                    <QuizExpandPanel
                      record={record}
                      navigation={navigation}
                      course={course}
                    />
                  </TableCell>
                </TableRow>
              )}
            </React.Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
};

export default ExpandableQuizTable;
